import React from 'react';
import { SPICE_LEVELS, SpiceLevel } from '../types/menu';

interface SpiceSelectorProps {
  selectedLevel: SpiceLevel | null;
  onChange: (level: SpiceLevel) => void;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

/**
 * Reusable spice level selector component
 * Shows buttons for all spice levels with visual indicators
 */
const SpiceSelector: React.FC<SpiceSelectorProps> = ({
  selectedLevel,
  onChange,
  size = 'medium',
  disabled = false
}) => {
  const sizeClasses = {
    small: 'text-xs py-1 px-2',
    medium: 'text-sm py-1.5 px-3',
    large: 'text-base py-2 px-4'
  };

  const buttonClass = sizeClasses[size];

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Spice Level
      </label>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(SPICE_LEVELS) as SpiceLevel[]).map((level) => {
          const spiceInfo = SPICE_LEVELS[level];
          const isSelected = selectedLevel === level;

          return (
            <button
              key={level}
              type="button"
              onClick={() => onChange(level)}
              disabled={disabled}
              className={`${buttonClass} rounded-lg border-2 transition-all duration-200 flex items-center gap-1.5 ${
                isSelected
                  ? 'border-current font-semibold shadow-md'
                  : 'border-gray-300 hover:border-gray-400 dark:border-gray-600'
              } ${
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105'
              }`}
              style={{
                color: isSelected ? spiceInfo.color : 'inherit',
                backgroundColor: isSelected ? `${spiceInfo.color}15` : 'transparent'
              }}
            >
              <span className="text-base">{spiceInfo.icon || '🔲'}</span>
              <span>{spiceInfo.displayName}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SpiceSelector;
